// ============================================================================
// design-identity.js — WHICH saved design is on screen, across a page reload
// ============================================================================
// CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-31)
//
// Ryan's rule: *"If I'm saving one or 10 designs in the WD, all 1-10 designs should have the same
// reference number as per the saved file in the WD… any time I push 1 or more items across, the
// saved file reference number in the WD determines the reference number in the project. **More
// pushes does not equal more reference numbers.**"*
//
// ── WHAT WAS BROKEN ────────────────────────────────────────────────────────────────────────────
// `ensureSaved()` (ui/project-push.js) POSTs `/wd-design-save` with no `id` whenever the app can't
// name the design it is pushing, and the worker then mints a fresh record AND a fresh `WEIGHT-` ref.
// The only thing that knew the id was `designsPanel.getCurrent()` — in-memory, so a page reload
// erased it. Every push after a reload therefore looked like a first save. His Lakesong project
// (`project:5flRmDXk`) carries the receipt: three `assets` rows, `WEIGHT-1003-A` / `1004-A` /
// `1005-A`, of which the first two name design records that no longer exist and contribute no tile.
// All nine render tiles are `WEIGHT-1005-A`. One design, one artwork set, three reference numbers.
//
// The worker was never at fault: `/wd-design-save` mints a ref only for a record that has none, and
// `/project-update` already de-dupes asset rows by `type + ref`. Three rows means three genuinely
// different refs arrived. So the fix is entirely here — give the app a memory that survives F5.
//
// ── WHY A SIBLING KEY AND NOT THE AUTOSAVE ENVELOPE ────────────────────────────────────────────
// ⛔ The obvious shape — stuff `{id, ref}` into `toAutosave()` — is the trap. That envelope is not
// private state: it is the string the SHARE LINK carries, the string the quote handoff posts, and
// the string the project tray stores once per artwork fingerprint and replays into a throwaway
// state to render each staged plate. Those envelopes get COPIED BETWEEN DESIGNS. An identity riding
// inside one would travel with a share link into a stranger's browser and would be replayed by the
// batch renderer, so the wrong ref would follow the wrong artwork — exactly the class of fault this
// module exists to close, re-armed on a wider surface.
//
// So identity is a SIBLING of the autosave, keyed in the same namespace, read and written only by
// the two places that legitimately know it: the designs panel (which owns "what is open") and boot
// (which decides whether the resumed work is still yours). Nothing serialises it into a payload.
//
// ── THE MIRROR-IMAGE BUG, WHICH IS WORSE ───────────────────────────────────────────────────────
// A remembered ref that OUTLIVES its design files brand-new work under an old reference number, and
// nobody would ever notice. So identity is cleared, not kept, at every point where the artwork on
// screen stops being the saved one: "+ New", declining Resume, a shared `?d=` link, opening a
// showcase example, deleting the record, and any wholesale `applyEnvelope` that doesn't re-adopt.
// When in doubt, forget: an un-adopted push mints one new ref and says so, which is recoverable.
// ============================================================================

/** Sibling of `AUTOSAVE_KEY = 'wd:v2:autosave'` (ui/state.js:52) — same app, same namespace. */
export const DESIGN_IDENTITY_KEY = 'wd:v2:design-identity';

/** Bumped if the shape changes. An unrecognised payload is treated as NO identity (the safe
 *  direction): the next push mints one ref and tells the rep its number. */
export const DESIGN_IDENTITY_VERSION = 1;

/**
 * The pure half — the same `{id, ref, name}` triple `designsPanel.getCurrent()` hands out.
 * Returns null unless there is a real server `id`, because an id is the only field that makes the
 * identity USABLE: `ensureSaved` short-circuits on it and `/wd-plate-upload` namespaces by it.
 * A ref without an id would be a label with nothing behind it.
 */
export function normaliseIdentity(rec) {
  const id = typeof rec?.id === 'string' ? rec.id.trim() : '';
  if (!id) return null;
  return {
    id,
    ref: typeof rec?.ref === 'string' ? rec.ref.trim() : null,
    name: typeof rec?.name === 'string' ? rec.name : '',
  };
}

// ---------------------------------------------------------------------------
// Storage shell — every call is total. Private mode, a full quota and a node
// test runner all have no `localStorage`, and none of them is a reason to fail
// an edit: the worst case is the pre-fix behaviour, which is a new ref.
// ---------------------------------------------------------------------------

function store() {
  try { return typeof localStorage === 'undefined' ? null : localStorage; }
  catch { return null; } // Safari throws on the mere ACCESS with cookies blocked
}

/** The design this browser last had open, or null. */
export function readDesignIdentity() {
  const ls = store();
  if (!ls) return null;
  try {
    const data = JSON.parse(ls.getItem(DESIGN_IDENTITY_KEY) || 'null');
    if (!data || data.v !== DESIGN_IDENTITY_VERSION) return null;
    return normaliseIdentity(data);
  } catch { return null; }
}

/** Remember it — or forget it, when `rec` names no saved design. One entry point for both, so
 *  "adopt whatever the panel now says" can be a single call at every site. */
export function writeDesignIdentity(rec) {
  const id = normaliseIdentity(rec);
  if (!id) return clearDesignIdentity();
  const ls = store();
  if (!ls) return;
  try { ls.setItem(DESIGN_IDENTITY_KEY, JSON.stringify({ v: DESIGN_IDENTITY_VERSION, ...id })); }
  catch { /* quota — the session keeps its in-memory copy, which is the pre-fix behaviour */ }
}

/** This work is no longer the saved design (see the mirror-image note in the header). */
export function clearDesignIdentity() {
  const ls = store();
  if (!ls) return;
  try { ls.removeItem(DESIGN_IDENTITY_KEY); } catch { /* private mode */ }
}
